import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import { useSesion } from '../sesion';
import { Cargando, Dialogo, Error } from '../componentes/comunes';

/**
 * Qué cubre la iguala, por área de trabajo y formato de tienda.
 *
 * Es la regla que decide si una orden se le cobra a Walmart. Antes lo
 * decía el tipo de iguala de la sucursal, y eso contestaba por todas sus
 * órdenes una pregunta que depende de dónde se trabajó: la misma Mi
 * Bodega cubre panadería y corte pero cobra la cocina.
 *
 * Se pinta como cuadrícula y no como lista de dieciocho renglones porque
 * la pregunta que se hace quien la mira es de dos ejes —«¿y en
 * Supercenter, el corte?»— y en una cuadrícula eso es cruzar un dedo por
 * la fila y otro por la columna. En lista habría que leer las dieciocho
 * buscando la que aplica.
 */

interface Celda {
  areaId: string;
  formatoTiendaId: string;
  reglaId: string | null;
  /** Nulo cuando nadie ha decidido esa combinación. */
  cubiertoPorIguala: boolean | null;
  vigenteDesde: string | null;
  nota: string | null;
}

interface Matriz {
  areas: { id: string; clave: string; nombre: string }[];
  formatos: { id: string; clave: string; nombre: string }[];
  celdas: Celda[];
}

interface Vigencia {
  id: string;
  cubiertoPorIguala: boolean;
  vigenteDesde: string;
  vigenteHasta: string | null;
  nota: string | null;
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function Cobertura() {
  const { puede } = useSesion();
  const [matriz, setMatriz] = useState<Matriz | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editando, setEditando] = useState<{
    celda: Celda; area: string; formato: string;
  } | null>(null);

  const administra = puede('cobertura', 'editar');

  const cargar = useCallback(() => {
    setError(null);
    api.get<Matriz>('/cobertura').then(setMatriz).catch(setError);
  }, []);

  useEffect(cargar, [cargar]);

  const celdaDe = (areaId: string, formatoId: string) =>
    matriz?.celdas.find((c) => c.areaId === areaId && c.formatoTiendaId === formatoId);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Cobertura de la iguala</h1>
          <p className="sub">
            Qué trabajo absorbe la iguala y cuál se le cobra al cliente,
            según el área y el formato de la tienda. De aquí sale, orden
            por orden, si lleva cargo aparte.
          </p>
        </div>
      </div>

      <Error error={error} />

      <div className="panel">
        {!matriz ? <Cargando /> : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Área</th>
                  {matriz.formatos.map((f) => <th key={f.id}>{f.nombre}</th>)}
                </tr>
              </thead>
              <tbody>
                {matriz.areas.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.nombre}</strong></td>
                    {matriz.formatos.map((f) => {
                      const celda = celdaDe(a.id, f.id);

                      return (
                        <td key={f.id}>
                          {/* Sin regla no se pinta ni «full» ni «con
                              costo»: nadie lo ha decidido, y ponerle una
                              de las dos etiquetas sería inventar la
                              respuesta. Una orden en esa combinación no
                              se puede cerrar hasta que se capture. */}
                          {celda?.cubiertoPorIguala === null || !celda ? (
                            administra ? (
                              <button
                                className="enlace"
                                onClick={() => celda && setEditando({
                                  celda, area: a.nombre, formato: f.nombre,
                                })}
                              >
                                Sin definir
                              </button>
                            ) : <span className="etiqueta no">Sin definir</span>
                          ) : administra ? (
                            <button
                              className="enlace"
                              onClick={() => setEditando({
                                celda, area: a.nombre, formato: f.nombre,
                              })}
                            >
                              <span className={`etiqueta ${celda.cubiertoPorIguala ? 'si' : ''}`}>
                                {celda.cubiertoPorIguala ? 'La cubre la iguala' : 'Con costo'}
                              </span>
                            </button>
                          ) : (
                            <span className={`etiqueta ${celda.cubiertoPorIguala ? 'si' : ''}`}>
                              {celda.cubiertoPorIguala ? 'La cubre la iguala' : 'Con costo'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="ayuda" style={{ marginTop: 12 }}>
              {administra
                ? 'Clica una celda para cambiarla o ver su historial. Un cambio'
                  + ' no reescribe lo anterior: se cierra la regla que estaba y'
                  + ' empieza una nueva hoy, para que una orden vieja se siga'
                  + ' explicando con la regla que le tocó.'
                : 'Sólo se puede consultar. Administrarla le corresponde a'
                  + ' Administración del sistema.'}
            </p>
          </>
        )}
      </div>

      {editando && (
        <CambiarCelda
          celda={editando.celda}
          area={editando.area}
          formato={editando.formato}
          onCerrar={() => setEditando(null)}
          onHecho={() => { setEditando(null); cargar(); }}
        />
      )}
    </>
  );
}

/**
 * Cambiar una celda, con su historial a la vista.
 *
 * El historial va aquí y no en otra pantalla porque es lo que contesta
 * «¿por qué esta orden de marzo se cobró?»: se mira desde cuándo aplica
 * lo que hay hoy y qué había antes.
 */
function CambiarCelda({ celda, area, formato, onCerrar, onHecho }: {
  celda: Celda;
  area: string;
  formato: string;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [cubierto, setCubierto] = useState(celda.cubiertoPorIguala ?? false);
  const [nota, setNota] = useState('');
  const [historial, setHistorial] = useState<Vigencia[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    api.get<Vigencia[]>('/cobertura/historial', {
      areaId: celda.areaId,
      formatoTiendaId: celda.formatoTiendaId,
    })
      .then(setHistorial)
      .catch(() => setHistorial([]));
  }, [celda.areaId, celda.formatoTiendaId]);

  const cambia = cubierto !== celda.cubiertoPorIguala;

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      await api.post('/cobertura', {
        areaId: celda.areaId,
        formatoTiendaId: celda.formatoTiendaId,
        cubiertoPorIguala: cubierto,
        nota: nota.trim() || undefined,
      });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={`${area} en ${formato}`}
      onCerrar={onCerrar}
      pie={
        <button className="primario" disabled={!cambia || trabajando} onClick={() => void guardar()}>
          {trabajando ? 'Guardando…' : 'Guardar el cambio'}
        </button>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="cubierto">Este trabajo…</label>
        <select
          id="cubierto"
          value={cubierto ? 'si' : 'no'}
          onChange={(e) => setCubierto(e.target.value === 'si')}
        >
          <option value="si">Lo cubre la iguala — no lleva cargo aparte</option>
          <option value="no">Se le cobra al cliente además de la iguala</option>
        </select>
      </div>

      {cambia && (
        <div className="campo">
          <label htmlFor="nota">Por qué cambia</label>
          <input
            id="nota" value={nota} maxLength={250}
            placeholder="Queda en la regla y en la bitácora"
            onChange={(e) => setNota(e.target.value)}
          />
          <span className="ayuda">
            El cambio aplica a partir de hoy. Las órdenes que ya se
            decidieron conservan la regla con la que se decidieron, así
            que sus facturas siguen cuadrando.
          </span>
        </div>
      )}

      <h3 style={{ marginTop: 16 }}>Qué se ha decidido aquí</h3>

      {!historial ? <Cargando /> : historial.length === 0 ? (
        <div className="vacio">Todavía nadie ha decidido esta combinación.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Desde</th>
              <th>Hasta</th>
              <th>Decisión</th>
              <th>Nota</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((v) => (
              <tr key={v.id}>
                <td>{fechaCorta(v.vigenteDesde)}</td>
                <td>{v.vigenteHasta ? fechaCorta(v.vigenteHasta) : 'vigente'}</td>
                <td>
                  <span className={`etiqueta ${v.cubiertoPorIguala ? 'si' : ''}`}>
                    {v.cubiertoPorIguala ? 'La cubre la iguala' : 'Con costo'}
                  </span>
                </td>
                <td>{v.nota ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialogo>
  );
}
